import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useToast } from '@razorpay/blade/components';
import api from 'api';
import { CreateEmployeeFieldInput } from 'api/gql-utils/employeeDataSettings/types';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';
import {
  buildCreateCustomFieldVariables,
  buildUpdateCustomFieldVariables,
  mapPayrollCustomFieldDefinition,
} from '../utils';
import { UpdateEmployeeFieldVariables } from '../types';

export const EMPLOYEE_FIELD_DEFINITIONS_QUERY_KEY = ['employee-data-settings', 'field-definitions'];
export const DEFAULT_EMPLOYEE_FIELD_PAGE_SIZE = 10;

const getErrorMessage = (error: unknown, fallback: string) => {
  const parsedError = parseGraphqlErrorFromExtensions(error as typeof AppError | AppError);
  return parsedError || fallback;
};

export const useEmployeeFieldDefinitions = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_EMPLOYEE_FIELD_PAGE_SIZE);
  const [pageTokens, setPageTokens] = useState<Record<number, string>>({ 0: '' });
  const pageToken = pageTokens[currentPage] ?? '';

  const fieldsQuery = useQuery(
    [...EMPLOYEE_FIELD_DEFINITIONS_QUERY_KEY, pageSize, pageToken],
    async () =>
      api.employeeDataSettings.queries.listCustomFieldDefinitions({
        pageSize,
        pageToken: pageToken || undefined,
      }),
    { keepPreviousData: true },
  );

  const pageInfo = fieldsQuery.data?.payrollCustomFieldDefinitions.pageInfo;
  const fields = useMemo(
    () =>
      (fieldsQuery.isPreviousData
        ? []
        : fieldsQuery.data?.payrollCustomFieldDefinitions.definitions ?? []
      )
        .map(mapPayrollCustomFieldDefinition)
        .sort((firstField, secondField) => firstField.displayOrder - secondField.displayOrder),
    [fieldsQuery.data, fieldsQuery.isPreviousData],
  );
  const totalFields = pageInfo?.total ?? fields.length;
  const nextPageToken = pageInfo?.nextPageToken ?? '';

  const filteredFields = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return fields;
    return fields.filter((field) =>
      [field.label, field.section, field.valueType, field.origin].some((value) =>
        value.toLowerCase().includes(normalizedSearch),
      ),
    );
  }, [fields, search]);

  const invalidateFieldDefinitions = () => {
    queryClient.invalidateQueries(EMPLOYEE_FIELD_DEFINITIONS_QUERY_KEY);
  };

  const createFieldMutation = useMutation(
    (input: CreateEmployeeFieldInput) =>
      api.employeeDataSettings.mutations.createCustomField(
        buildCreateCustomFieldVariables(input, totalFields + 1),
      ),
    {
      onSuccess: () => {
        invalidateFieldDefinitions();
        toast.show({ content: 'Employee field added', color: 'positive' });
      },
      onError: (error) => {
        toast.show({
          content: getErrorMessage(error, 'Could not add employee field'),
          color: 'negative',
        });
      },
    },
  );

  const updateFieldMutation = useMutation(
    ({ fieldKey, input, displayOrder }: UpdateEmployeeFieldVariables) =>
      api.employeeDataSettings.mutations.updateCustomField(
        buildUpdateCustomFieldVariables(fieldKey, input, displayOrder),
      ),
    {
      onSuccess: () => {
        invalidateFieldDefinitions();
        toast.show({ content: 'Employee field updated', color: 'positive' });
      },
      onError: (error) => {
        toast.show({
          content: getErrorMessage(error, 'Could not update employee field'),
          color: 'negative',
        });
      },
    },
  );

  const deleteFieldMutation = useMutation(
    (fieldKey: string) => api.employeeDataSettings.mutations.deleteCustomField({ fieldKey }),
    {
      onSuccess: () => {
        invalidateFieldDefinitions();
        toast.show({ content: 'Employee field deleted', color: 'positive' });
      },
      onError: (error) => {
        toast.show({
          content: getErrorMessage(error, 'Could not delete employee field'),
          color: 'negative',
        });
      },
    },
  );

  const onPageChange = ({ page }: { page: number }) => {
    if (page === currentPage) return;
    if (page === 0 || pageTokens[page] !== undefined) {
      setCurrentPage(page);
      return;
    }
    if (page === currentPage + 1 && nextPageToken) {
      setPageTokens((currentTokens) => ({ ...currentTokens, [page]: nextPageToken }));
      setCurrentPage(page);
    }
  };

  const onPageSizeChange = ({ pageSize: nextPageSize }: { pageSize: number }) => {
    setPageSize(nextPageSize);
    setCurrentPage(0);
    setPageTokens({ 0: '' });
  };

  return {
    fields,
    filteredFields,
    search,
    setSearch,
    currentPage,
    pageSize,
    totalFields,
    isLoading: fieldsQuery.isLoading || fieldsQuery.isPreviousData,
    isFetching: fieldsQuery.isFetching,
    isError: fieldsQuery.isError,
    refetch: fieldsQuery.refetch,
    onPageChange,
    onPageSizeChange,
    createFieldMutation,
    updateFieldMutation,
    deleteFieldMutation,
  };
};
